import { useEffect, useRef, useState } from 'react';
import { EdugenFamilyService } from '../services/edugenFamily';
import { resolveUploadsUrl } from '../utils/resolveUploadsUrl';
import AlumniConnectModal from './AlumniConnectModal';

// Shown until an admin has published real alumni/testimonials from the
// dashboard, so the section is never empty during rollout.
const FALLBACK_ALUMNI = [
  {
    img: "/images/alumani/damia.jpg",
    name: "Avni",
    bio: "Meet Avni, a proud Edugen alumni who turned her Canadian study dream into reality. With expert support for university applications, documentation and visa processing, she secured admission to the University of Manitoba and successfully received her Canadian student visa."
  },
  {
    img: "/images/alumani/aakash.jpg",
    name: "Aakash",
    bio: "From cracking his IELTS score to landing a spot at Seneca College, Toronto, Aakash's journey was backed by Edugen at every turn — from test prep through to the final visa approval. He's now on his way to Canada, ready for the next chapter."
  },
  {
    img: "/images/alumani/avni.jpg",
    name: "Damia",
    bio: "Damia set her sights on Newcastle University in the UK, and with Edugen's guidance on IELTS prep and the entire visa process, that goal became a reality. Her UK student visa is approved, and her study abroad journey is officially underway."
  },
  {
    img: "/images/alumani/al_nam.webp",
    name: "Shaurya",
    bio: "What began as IELTS preparation with Edugen eventually led Shaurya all the way to an offer from the University of Regina and an approved Canadian student visa. His journey stands as proof of what consistent guidance and hard work can achieve together."
  },
  {
    img: "/images/alumani/sandeep.jpg",
    name: "Sandeep",
    bio: "Sandeep's path to University Canada West started with focused IELTS training at Edugen, followed by steady support through every stage of his application and visa process. Today, he holds a Canadian student visa and is set to begin his academic journey with real confidence."
  }
];

const FALLBACK_TESTIMONIALS = [
  {
    img: "/images/alumani/damia.jpg",
    name: "Avni",
    role: "University of Manitoba, Canada",
    text: "Honestly, I didn't expect the whole process to go this smoothly. My IELTS trainer at Edugen sat with me one-on-one and really pushed me till I hit an 8.5 overall, which I wasn't even aiming that high for. Then when it came to applying to University of Manitoba and sorting out the Canada visa, the team just stayed on top of it with me the whole time — never felt like I was figuring things out alone. Got my offer, got my visa, and honestly still can't believe it worked out this well. Huge thanks to everyone at Edugen for this.",
    date: "IELTS Band Score: 8.5",
    time: "Visa Approved"
  },
  {
    img: "/images/alumani/aakash.jpg",
    name: "Aakash",
    role: "Seneca College, Toronto",
    text: "Edugen helped me a lot with my IELTS prep, and honestly, they were right there with me through the whole Canada visa process too. Whenever I had questions, I actually got straight answers — no runaround. That kind of steady support is what got me into Seneca College in Toronto, and eventually my Canadian student visa came through too.",
    date: "IELTS + Visa Prep",
    time: "Visa Approved"
  },
  {
    img: "/images/alumani/avni.jpg",
    name: "Damia",
    role: "Newcastle University, UK",
    text: "Edugen made a real difference in my UK application. They helped me prepare for IELTS and stayed with me through every stage that followed, always ready to answer questions when I got stuck. In the end, it all came together — I got my UK student visa and a spot at Newcastle University.",
    date: "IELTS + Visa Prep",
    time: "Visa Approved"
  },
  {
    img: "/images/alumani/al_nam.webp",
    name: "Shaurya",
    role: "University of Regina, Canada",
    text: "From IELTS prep all the way through to getting my Canadian student visa, Edugen kept the whole process simple and never let it feel overwhelming. With their steady guidance behind me, I got into the University of Regina and felt genuinely ready to start this new chapter in Canada.",
    date: "IELTS + Visa Prep",
    time: "Visa Approved"
  },
  {
    img: "/images/alumani/sandeep.jpg",
    name: "Sandeep",
    role: "University Canada West, Canada",
    text: "Edugen walked me through everything, from IELTS prep right up to my Canadian student visa, and made sure the whole process actually made sense to me instead of feeling like a maze. With their support behind me, I confidently got into University Canada West and I'm ready to start this new chapter in Canada.",
    date: "IELTS + Visa Prep",
    time: "Visa Approved"
  }
];

// The carousel's translate-x/width/height values below are calibrated for a
// 5-card layout, so the fetched lists are capped at 5 to keep the exact same
// visual positions the design was built for — fewer than 5 just leaves some
// slots empty, which still looks fine.
const MAX_CARDS = 5;

const EdugenFamilySection = () => {
  const [familyMembers, setFamilyMembers] = useState(FALLBACK_ALUMNI);
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIALS);
  const [activeIndex, setActiveIndex] = useState(2);
  const [familyView, setFamilyView] = useState('alumni');
  const [activeTestIndex, setActiveTestIndex] = useState(1);
  // Name of the alumnus whose "Connect" form is open, or null when closed.
  const [connectAlumni, setConnectAlumni] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [alumniResult, testimonialResult] = await Promise.allSettled([
        EdugenFamilyService.listPublic('alumni'),
        EdugenFamilyService.listPublic('testimonial'),
      ]);

      if (cancelled) return;

      if (alumniResult.status === 'fulfilled') {
        const rows = Array.isArray(alumniResult.value?.data) ? alumniResult.value.data : [];
        if (rows.length) {
          setFamilyMembers(rows.slice(0, MAX_CARDS).map((row) => ({
            img: resolveUploadsUrl(row.photo) || row.photo,
            name: row.name,
            bio: row.bio,
          })));
        }
      } else {
        console.error('Failed to load Edugen alumni:', alumniResult.reason);
      }

      if (testimonialResult.status === 'fulfilled') {
        const rows = Array.isArray(testimonialResult.value?.data) ? testimonialResult.value.data : [];
        if (rows.length) {
          setTestimonials(rows.slice(0, MAX_CARDS).map((row) => ({
            img: resolveUploadsUrl(row.photo) || row.photo,
            name: row.name,
            role: row.role,
            text: row.quote_text,
            date: row.meta_label_1,
            time: row.meta_label_2,
          })));
        }
      } else {
        console.error('Failed to load Edugen testimonials:', testimonialResult.reason);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const getCardStyle = (index) => {
    const offset = (index - activeIndex + 5) % 5;

    // 0: Center, 1: Right, 2: Far Right, 3: Far Left, 4: Left
    switch(offset) {
      case 0: // Center
        return "z-20 w-[410px] h-[480px] translate-x-0 opacity-100";
      case 1: // Right
        return "z-10 w-[410px] h-[440px] translate-x-[280px] opacity-90";
      case 2: // Far Right
        return "z-0 w-[320px] h-[360px] translate-x-[560px] opacity-90";
      case 3: // Far Left
        return "z-0 w-[320px] h-[360px] -translate-x-[560px] opacity-90";
      case 4: // Left
        return "z-10 w-[410px] h-[440px] -translate-x-[280px] opacity-90";
      default:
        return "";
    }
  };

  const goToAlumni = (dir) => setActiveIndex((i) => (i + dir + familyMembers.length) % familyMembers.length);
  const goToTestimonial = (dir) => setActiveTestIndex((i) => (i + dir + testimonials.length) % testimonials.length);

  // A fast mouse sweep across the row fires onMouseEnter on every card it
  // passes over, each one redirecting the in-flight slide animation before it
  // finishes — that's what actually reads as "too fast", not the transition
  // duration itself. Debouncing so only the card the cursor briefly rests on
  // triggers a slide fixes that without needing a hover-vs-click redesign.
  const alumniHoverTimer = useRef(null);
  const testimonialHoverTimer = useRef(null);
  // mouseenter also fires when a card moves under a *stationary* cursor — while
  // the page scrolls, or when the previous slide carries the next card under
  // it. That made the carousel chase itself endlessly, so the centre card was
  // always mid-transition (blank) until the user hovered it. Only slides caused
  // by the pointer genuinely moving (browser-synthesised moves carry zero
  // movementX/Y) are allowed.
  const lastRealPointerMove = useRef(0);
  useEffect(() => {
    const onPointerMove = (e) => {
      if (e.movementX || e.movementY) lastRealPointerMove.current = performance.now();
    };
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => window.removeEventListener('pointermove', onPointerMove);
  }, []);
  const pointerJustMoved = () => performance.now() - lastRealPointerMove.current < 150;
  const handleAlumniHover = (index) => {
    clearTimeout(alumniHoverTimer.current);
    if (!pointerJustMoved()) return;
    alumniHoverTimer.current = setTimeout(() => setActiveIndex(index), 220);
  };
  const handleTestimonialHover = (index) => {
    clearTimeout(testimonialHoverTimer.current);
    if (!pointerJustMoved()) return;
    testimonialHoverTimer.current = setTimeout(() => setActiveTestIndex(index), 220);
  };
  useEffect(() => () => {
    clearTimeout(alumniHoverTimer.current);
    clearTimeout(testimonialHoverTimer.current);
  }, []);

  const getTestimonialStyle = (index) => {
    const offset = (index - activeTestIndex + testimonials.length) % testimonials.length;
    switch (offset) {
      case 0: // Center
        return "z-20 w-[860px] h-[340px] translate-x-0 opacity-100";
      case 1: // Right
        return "z-10 w-[360px] h-[300px] translate-x-[480px] opacity-60";
      case 2: // Far Right
        return "z-0 w-[280px] h-[240px] translate-x-[760px] opacity-40";
      case testimonials.length - 2: // Far Left
        return "z-0 w-[280px] h-[240px] -translate-x-[760px] opacity-40";
      case testimonials.length - 1: // Left
        return "z-10 w-[360px] h-[300px] -translate-x-[480px] opacity-60";
      default:
        return "z-0 w-[240px] h-[200px] opacity-0 pointer-events-none";
    }
  };

  return (
    <section className="pt-6 pb-6 overflow-hidden bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="text-[40px] font-bold text-[#0E2A46] font-['Poppins']">
          The Edugen Family Around the World
        </h2>

        {/* Toggle */}
        <div className="mt-4 flex justify-center">
          <div className="flex items-center gap-2 rounded-full p-1">
            <button
              onClick={() => setFamilyView('alumni')}
              className="min-w-[86px] h-10 rounded-[50px] py-3 px-4 font-medium font-['Poppins'] bg-[#414B48] text-white"
            >
              Alumni
            </button>
            <button
              onClick={() => setFamilyView('testimonials')}
              className="min-w-[122px] h-10 rounded-[50px] py-3 px-4 font-medium font-['Poppins'] bg-white text-[#D1471C] border border-[#FFFFFF66] shadow-sm"
            >
              Testimonials
            </button>
          </div>
        </div>

        {/* 3D Cards Carousel (desktop only — unchanged) */}
        {familyView === 'alumni' ? (
          <>
          <div className="hidden lg:flex mt-0 justify-center items-center h-[490px] relative">
            {familyMembers.map((member, index) => {
              const offset = (index - activeIndex + 5) % 5;
              const isCenter = offset === 0;
              return (
                <div
                  key={index}
                  onMouseEnter={() => handleAlumniHover(index)}
                  className={`absolute rounded-[16px] border-2 border-white overflow-hidden shadow-xl transition-all duration-1000 ease-in-out cursor-pointer bg-[#D9D9D9] ${getCardStyle(index)}`}
                >
                  <img
                    src={member.img}
                    alt="Member"
                    className="w-full h-full object-cover"
                    style={isCenter ? undefined : { mixBlendMode: 'luminosity', backgroundColor: '#D9D9D9' }}
                    loading="lazy"
                  />
                  <div
                    className={`absolute left-0 right-0 mx-auto w-[357.93px] max-w-[calc(100%-16px)] rounded-[16px] transition-all duration-1000 ease-in-out overflow-hidden ${
                      isCenter
                        ? 'bottom-8 shadow-lg group cursor-default hover:h-[280px] h-[64px] flex flex-col justify-start pt-6 px-6 z-40 bg-[#009183]/50'
                        : 'bottom-6 h-[64px] flex items-center justify-center'
                    }`}
                    style={isCenter ? { backdropFilter: 'blur(30px)', WebkitBackdropFilter: 'blur(30px)' } : { backgroundColor: '#40474980', backdropFilter: 'blur(30px)', WebkitBackdropFilter: 'blur(30px)' }}
                  >
                    <p className={`font-['Poppins'] font-medium text-[20px] leading-[32px] tracking-[-0.18px] text-white text-center align-middle transition-all duration-300 ${
                      isCenter ? 'group-hover:mb-4' : 'text-base leading-normal tracking-normal'
                    }`}>
                      {member.name}
                    </p>
                    {isCenter && (
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100 flex flex-col items-center gap-4 w-full pb-6 pt-2">
                        <p className="text-white/90 text-center text-base leading-relaxed line-clamp-4">
                          {member.bio}
                        </p>
                        <button type="button" onClick={() => setConnectAlumni(member.name)} className="bg-[#C6521C] hover:bg-[#A9461A] text-white font-semibold py-2 px-8 rounded-full w-full transition-colors shadow-md text-sm">
                          Connect
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="hidden lg:flex mt-4 items-center justify-center gap-2">
            {familyMembers.map((_, i) => (
              <span key={i} className={`w-2 h-2 rounded-full ${i === activeIndex ? 'bg-[#FF5722]' : 'bg-gray-300'}`} />
            ))}
          </div>

          {/* Mobile/tablet: single-card carousel, sized to fit the viewport with no horizontal scroll */}
          <div className="lg:hidden mt-6">
            {familyMembers[activeIndex] && (
              <div className="relative mx-auto w-full max-w-[320px] sm:max-w-sm h-[420px] rounded-[20px] overflow-hidden shadow-xl">
                <img src={familyMembers[activeIndex].img} alt={familyMembers[activeIndex].name} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute inset-x-4 bottom-4 bg-[#174035]/85 backdrop-blur-md rounded-xl p-4">
                  <p className="text-white font-bold text-lg text-center">{familyMembers[activeIndex].name}</p>
                  <p className="mt-2 text-white/90 text-sm text-center leading-relaxed line-clamp-4">{familyMembers[activeIndex].bio}</p>
                  <button type="button" onClick={() => setConnectAlumni(familyMembers[activeIndex].name)} className="mt-3 bg-[#C6521C] hover:bg-[#A9461A] text-white font-semibold py-2 px-8 rounded-full w-full transition-colors shadow-md text-sm">
                    Connect
                  </button>
                </div>
              </div>
            )}
            <div className="mt-4 flex items-center justify-center gap-4">
              <button type="button" onClick={() => goToAlumni(-1)} aria-label="Previous alumni" className="w-10 h-10 shrink-0 rounded-full border border-[#FF5722] text-[#FF5722] flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <div className="flex gap-2">
                {familyMembers.map((_, i) => (
                  <span key={i} className={`w-2 h-2 rounded-full ${i === activeIndex ? 'bg-[#FF5722]' : 'bg-gray-300'}`} />
                ))}
              </div>
              <button type="button" onClick={() => goToAlumni(1)} aria-label="Next alumni" className="w-10 h-10 shrink-0 rounded-full border border-[#FF5722] text-[#FF5722] flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>
          </>
        ) : (
          <>
          <div className="hidden lg:flex mt-6 justify-center items-center h-[420px] relative">
            {testimonials.map((t, index) => {
              const isCenter = index === activeTestIndex;
              return (
                <div
                  key={index}
                  onMouseEnter={() => handleTestimonialHover(index)}
                  className={`absolute rounded-[20px] overflow-hidden shadow-xl transition-all duration-1000 ease-in-out bg-white ${getTestimonialStyle(index)}`}
                >
                  {/* Both layouts stay mounted and crossfade. Swapping them instantly while the
                      card's size animates made the incoming centre card squeeze its full quote
                      into a narrow box mid-slide. The centre layout is a fixed 860px wide (the
                      centre card's final width) so the card simply clips it while growing
                      instead of the text reflowing. */}
                  <div className={`absolute inset-y-0 left-1/2 -translate-x-1/2 w-[860px] flex items-center gap-6 px-6 py-4 bg-[#FFF6EA] transition-opacity ${isCenter ? 'opacity-100 duration-150' : 'opacity-0 duration-150 pointer-events-none'}`}>
                      <div className="w-[280px] h-[280px] shrink-0 rounded-[16px] bg-[#EDEDED] overflow-hidden flex items-center justify-center">
                        <img src={t.img} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div className="flex-1">
                        <div className="text-[#FF5722] text-2xl">&ldquo;</div>
                        <div className="mt-1 text-[#0B1C33] font-bold text-xl font-['Poppins']">{t.name}</div>
                        <div className="text-[#4A5568] text-sm font-['Inter']">{t.role}</div>
                        <p className="mt-3 text-[#1A202C] text-sm leading-relaxed font-['Inter']">{t.text}</p>
                        <div className="mt-4 flex items-center gap-6 text-[#4A5568] text-sm font-['Inter']">
                          <span className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-[#FF5722]" fill="currentColor" viewBox="0 0 20 20"><path d="M6 2a1 1 0 000 2h1v1a1 1 0 102 0V4h2v1a1 1 0 102 0V4h1a1 1 0 100-2H6zM4 7a2 2 0 012-2h8a2 2 0 012 2v7a2 2 0 01-2 2H6a2 2 0 01-2-2V7zm2 2a1 1 0 100 2h8a1 1 0 100-2H6z"/></svg>
                            {t.date}
                          </span>
                          <span className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-[#FF5722]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-7a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                            {t.time}
                          </span>
                        </div>
                      </div>
                  </div>
                  <div className={`absolute inset-0 flex items-center justify-center bg-white transition-opacity ${isCenter ? 'opacity-0 duration-150 pointer-events-none' : 'opacity-100 duration-150'}`}>
                      <div className="w-[300px] h-[280px] rounded-[16px] overflow-hidden bg-[#EDEDED]">
                        <img src={t.img} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                  </div>
                </div>
              );
            })}
            <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
              {testimonials.map((_, i) => (
                <span
                  key={i}
                  className={`w-2 h-2 rounded-full ${i === activeTestIndex ? 'bg-[#FF5722]' : 'bg-gray-300'}`}
                />
              ))}
            </div>
          </div>

          {/* Mobile/tablet: single-card testimonial, stacked layout that fits the viewport */}
          <div className="lg:hidden mt-6">
            {testimonials[activeTestIndex] && (
              <div className="mx-auto w-full max-w-[320px] sm:max-w-sm rounded-[20px] overflow-hidden shadow-xl bg-[#FFF6EA] p-5">
                <div className="w-full h-[200px] rounded-[16px] overflow-hidden bg-[#EDEDED]">
                  <img src={testimonials[activeTestIndex].img} alt={testimonials[activeTestIndex].name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="mt-4 text-center">
                  <div className="text-[#FF5722] text-2xl">&ldquo;</div>
                  <div className="text-[#0B1C33] font-bold text-lg font-['Poppins']">{testimonials[activeTestIndex].name}</div>
                  <div className="text-[#4A5568] text-sm font-['Inter']">{testimonials[activeTestIndex].role}</div>
                  <p className="mt-3 text-[#1A202C] text-sm leading-relaxed font-['Inter'] text-left">{testimonials[activeTestIndex].text}</p>
                  <div className="mt-4 flex items-center justify-center gap-6 text-[#4A5568] text-xs font-['Inter'] flex-wrap">
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-[#FF5722]" fill="currentColor" viewBox="0 0 20 20"><path d="M6 2a1 1 0 000 2h1v1a1 1 0 102 0V4h2v1a1 1 0 102 0V4h1a1 1 0 100-2H6zM4 7a2 2 0 012-2h8a2 2 0 012 2v7a2 2 0 01-2 2H6a2 2 0 01-2-2V7zm2 2a1 1 0 100 2h8a1 1 0 100-2H6z"/></svg>
                      {testimonials[activeTestIndex].date}
                    </span>
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-[#FF5722]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-7a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                      {testimonials[activeTestIndex].time}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div className="mt-4 flex items-center justify-center gap-4">
              <button type="button" onClick={() => goToTestimonial(-1)} aria-label="Previous testimonial" className="w-10 h-10 shrink-0 rounded-full border border-[#FF5722] text-[#FF5722] flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <div className="flex gap-2">
                {testimonials.map((_, i) => (
                  <span key={i} className={`w-2 h-2 rounded-full ${i === activeTestIndex ? 'bg-[#FF5722]' : 'bg-gray-300'}`} />
                ))}
              </div>
              <button type="button" onClick={() => goToTestimonial(1)} aria-label="Next testimonial" className="w-10 h-10 shrink-0 rounded-full border border-[#FF5722] text-[#FF5722] flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>
          </>
        )}
      </div>
      <AlumniConnectModal
        open={connectAlumni !== null}
        alumniName={connectAlumni}
        onClose={() => setConnectAlumni(null)}
      />
    </section>
  );
};

export default EdugenFamilySection;
